import type Highcharts from 'highcharts';

import type { DChartInterface } from 'webapp-types';
import { shadeColor } from 'webapp/utils/color';

import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import FunnelOrPipelineBaseRenderer from 'webapp/d-chart/renderer/funnel-or-pipeline-base';
import type {
	AxisWithPlotLinesAndBands,
	PointWithDataLabel,
} from 'webapp/d-chart/renderer/funnel-or-pipeline-base';
import type DChartSeries from 'webapp/d-chart/series/series';

export default class FunnelRenderer extends FunnelOrPipelineBaseRenderer {
	axisForOverallConversion = 'yAxis' as const;

	override tooltipArrowPosition = 'left'; // bcos we invert the axis

	override get visualization() {
		return super.visualization;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;

		let options = super.baseOptions;

		options = this.mergeOptions(options, this.commmonBaseOptions, {
			chart: {
				type: 'areaspline',
				inverted: true,
				marginRight: 0,
				marginLeft: 0,
				events: {
					click: function (this: Highcharts.Chart, e: Highcharts.PointerEventObject) {
						const yPos = this.xAxis[0]?.toValue(e.chartY);
						if (typeof yPos === 'number') {
							that.sendEventForBlockContentClickFromPosition(yPos);
						}

						if (this.hoverPoint) {
							const { dbxPoint, graphic } = this
								.hoverPoint as DChartInterface.ChartHoverPoint;
							that.onPointClick(dbxPoint, e, graphic?.element);
						}
					},
				},
			},
			yAxis: [
				{
					width: '80%',
					labels: {
						enabled: false,
					},
				},
			],
			plotOptions: {
				// trackByArea is an area series option, applied to all series from here
				series: {
					trackByArea: true,
					stacking: 'normal',
				} as Highcharts.PlotSeriesOptions,
				areaspline: {
					events: {
						click: function (
							// a series has no dbxPoint / graphic of its own, so the event's point is used
							this: Highcharts.Series & {
								dbxPoint?: DChartPrimaryPoint;
								graphic?: Highcharts.SVGElement;
							},
							e: Highcharts.SeriesClickEventObject,
						) {
							const dbxPoint: DChartPrimaryPoint =
								this.dbxPoint ||
								(e.point as DChartInterface.ChartHoverPoint).dbxPoint;
							const graphic: Highcharts.SVGElement | undefined =
								this.graphic || e.point.graphic;

							that.onPointClick(dbxPoint, e, graphic?.element);
						},
					},
				},
			},
		});

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		const m = this.dChart.sizeModifier;
		let options = super.sizeOptions;

		options = this.mergeOptions(options, {
			chart: {
				spacingTop: 8 + m, // to not cut off top labels
				spacingBottom: m / 2, // to not cut off bottom labels if no legend
			},
			yAxis: [
				{
					title: {
						margin: m,
					},
				},
			],
		});

		return options;
	}

	protected override getPointsDataForSeries(
		series: DChartSeries,
	): DChartInterface.ChartDataPoint[] {
		const pointsData = super.getPointsDataForSeries(series);

		// add plotLineId to know to which plotline we will show this dataLabel on
		pointsData.forEach((pointData, idx) => {
			pointData.custom = {
				plotLineId: idx,
			};
		});

		if (pointsData.length === 1) {
			// add extra point so there is some actual chart (rectangle) visible
			pointsData.push(Object.assign({}, pointsData[0]));
		}

		return pointsData;
	}

	protected preparePlotLinesAndBands() {
		const plotBands: DChartInterface.PlotBandOptions[] = [],
			plotLines: Highcharts.XAxisPlotLinesOptions[] = [];

		this.series.forEach((series) => {
			if (series.isMain) {
				series.points.forEach((point, idx) => {
					const isFirstStep = !point.index;

					// adding plot bands - just once
					// first band has no height
					const plotBandEndIndex = isFirstStep
						? 0
						: point.index * this.pointsIncludingFakePerActualData;
					if (!plotBands.find((item) => item.to === plotBandEndIndex)) {
						plotBands.push({
							dbxPoint: point,
							className: 'highcharts-dbx-custom-plot-band',
							from: isFirstStep
								? 0
								: (point.index - 1) * this.pointsIncludingFakePerActualData,
							to: plotBandEndIndex,
							label: {
								useHTML: true,
								align: 'right',
								verticalAlign: 'bottom',
								text: this.formatPlotBandText(point),
							},
						});
					}

					// adding plot lines - just once
					const plotLineValue = idx * this.pointsIncludingFakePerActualData;
					if (!plotLines.find((item) => item.value === plotLineValue)) {
						plotLines.push({
							className: 'highcharts-dbx-custom-plot-line',
							label: {
								useHTML: true,
								align: 'left',
								verticalAlign: 'top',
								text: this.formatPlotLineText(point),
							},
							value: plotLineValue,
							zIndex: 4,
						});
					}
				});
			}
		});

		return {
			plotBands,
			plotLines,
		};
	}

	protected getSeriesGradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData,
			customColorClassMap = this.dChart.customColorClassMap,
			themeId = themeData.themeId,
			colors = themeData.colors || [],
			primaryColors = themeData.visualizationConfig.order || [],
			color1 = primaryColors[0],
			color2 = primaryColors[1],
			blockId = this.visualization.block.id;

		const defs: DChartInterface.Def[] = [
			{
				tagName: 'linearGradient',
				id: `gradient-funnel-compare-series-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 1,
				y2: 0,
				children: [
					{
						tagName: 'stop',
						offset: '0%',
						'stop-color': color1 ? shadeColor(colors[color1]!, 30) : '',
						'stop-opacity': 0.4,
					},
					{
						tagName: 'stop',
						offset: '100%',
						'stop-color': color2 ? shadeColor(colors[color2]!, 30) : '',
						'stop-opacity': 0.4,
					},
				],
			},
		];

		const addDefForParams = (idPostfix: string, color: string) => {
			const id = `gradient-funnel-main-series-${idPostfix}`;
			defs.push({
				tagName: 'linearGradient',
				id,
				x1: 0,
				y1: 0,
				x2: 1,
				y2: 0,
				children: [
					{
						tagName: 'stop',
						offset: '0%',
						'stop-color': color,
					},
					{
						tagName: 'stop',
						offset: '100%',
						'stop-color': shadeColor(color, 30),
					},
				],
			});
		};

		primaryColors.forEach((colorIndex: number) => {
			addDefForParams(`${themeId}-${colorIndex}`, colors[colorIndex]!);
		});

		Object.entries(customColorClassMap).forEach((entry) => {
			const [hex, customClass] = entry;
			addDefForParams(`${blockId}-${customClass}`, hex);
		});

		return defs;
	}

	protected updateDynamicElementsOnRender(hChart: Highcharts.Chart): void {
		if (!hChart.series.length) {
			return;
		}

		const hChartPlotWidth = hChart.plotWidth,
			xAxis = hChart.xAxis[0],
			plotLinesAndBands = (xAxis as AxisWithPlotLinesAndBands).plotLinesAndBands,
			plotLines = plotLinesAndBands.filter(
				(item) => item.options.className === 'highcharts-dbx-custom-plot-line',
			);

		// positioning bubbles (step conversions)
		{
			const dataItemsWithDataLabels: PointWithDataLabel[] = [];

			hChart.series.forEach((s) => {
				dataItemsWithDataLabels.push(
					...(s.data as PointWithDataLabel[]).filter((item) => !!item.dataLabel),
				);
			});

			const showBubblesInCenter = this.shownMainSeriesCount < 2;

			// Finding right plotline to put dataLabel on
			plotLines.forEach((plotLine, plotLineIndex) => {
				let currenDataLabel: PointWithDataLabel['dataLabel'];
				dataItemsWithDataLabels.forEach((dataItem) => {
					if (plotLineIndex === dataItem.custom.plotLineId) {
						// get dataLabel on the beginning of plot band
						const dataLabel = dataItem.dataLabel;

						// calculating dataLabel position to make sure its centered vertically in series area.
						if (dataLabel) {
							const seriesThickness = dataItem.yBottom - dataItem.plotY,
								showAtX = dataItem.x - (showBubblesInCenter ? 0 : 1),
								pxFromTop =
									hChart.xAxis[0]!.toPixels(showAtX, false) - hChart.plotTop;

							const pxFromLeft = showBubblesInCenter
								? hChartPlotWidth / 2
								: hChart.yAxis[0]!.toPixels(dataItem.stackY, false) -
									0.5 * seriesThickness;

							const newDataLabelPosition = {
								x: Math.max(0, pxFromLeft - 0.5 * dataLabel.width),
								y: pxFromTop - 0.5 * dataLabel.height,
							};

							if (this.animateBubblePath && this.dChart.isAnimated) {
								dataLabel.animate(newDataLabelPosition, {
									duration: this.animationDuration,
								});
							} else {
								dataLabel.attr(newDataLabelPosition); // to prevent floating bubbles from initial position (when we change step conversion or switching show percent)
							}

							currenDataLabel = dataLabel;
						}
					}
				});

				let plotLineMaxWidth = hChartPlotWidth / 2 - hChart.plotLeft;
				if (currenDataLabel) {
					plotLineMaxWidth = plotLineMaxWidth - currenDataLabel.width;
					currenDataLabel = undefined;
				}

				if (plotLine.label?.element) {
					plotLine.label.element.style.maxWidth = `${plotLineMaxWidth}px`; // plotLeft because of spacing applied; maxWidth because of ellipsis on step name
				}
			});
		}

		this.animateBubblePath = false;

		// positioning step labels (left side)
		{
			const themeId = this.dChart.themeData.themeId;
			const isGreyscaleTheme = themeId === 4 || themeId === 5; // greyscale-white or greyscale-black

			plotLines.forEach((plotLine, _index: number) => {
				const plotLineLabel = plotLine.label;

				if (plotLineLabel) {
					const plotLineValue = plotLine.options.value,
						plotLineLabelY =
							xAxis!.toPixels(plotLineValue!, false) -
							plotLineLabel.element.offsetHeight / 2;

					if (this.animateBubblePath && this.dChart.isAnimated) {
						plotLineLabel.animate(
							{ y: plotLineLabelY },
							{ duration: this.animationDuration },
						);
					} else {
						plotLineLabel.attr({ y: plotLineLabelY });
					}

					// apply grey color to labels for greyscale themes to improve readability
					if (isGreyscaleTheme && plotLineLabel.element) {
						const nameElement = plotLineLabel.element.querySelector('.name');
						if (nameElement) {
							(nameElement as HTMLElement).style.color = 'var(--databox-neutral-400)';
						}
					}
				}
			});
		}

		// Positioning label with values (right side)
		{
			const m = this.dChart.sizeModifier,
				plotBands = plotLinesAndBands.filter(
					(item) => item.options.className === 'highcharts-dbx-custom-plot-band',
				);
			plotBands.forEach((plotBand) => {
				const plotBandLabel = plotBand.label;

				if (plotBandLabel) {
					// Highcharts 12 subtracts the font baseline from alignAttr.y; add it back
					const y =
						plotBandLabel.alignAttr.y +
						hChart.renderer.fontMetrics(plotBandLabel).b -
						m * 1.3;

					if (this.animateBubblePath && this.dChart.isAnimated) {
						plotBandLabel.animate({ y }, { duration: this.animationDuration });
					} else {
						plotBandLabel.attr({ y });
					}
				}
			});
		}

		// centering axis title (overall conversion)
		{
			const yAxisTitle = (
				hChart.yAxis[0] as Highcharts.Axis & { axisTitle?: Highcharts.SVGElement }
			).axisTitle; // a Highcharts internal
			// Setting yAxisTitle left pos to 20% and width to 60% of the chart width
			// bcos the plot area itself is only 80% (we center the title with css)
			if (yAxisTitle?.element) {
				yAxisTitle.element.style.left = `${this.dChart.maxWidth * 0.2}px`;
				yAxisTitle.element.style.width = `${this.dChart.maxWidth * 0.6}px`;
			}
		}
	}

	private formatPlotLineText(point: DChartPrimaryPoint): string {
		const pointIndex = point.index,
			stepPoint = this.visualization.sortedSteps[pointIndex]?.totalPoint;
		if (stepPoint) {
			return `<div class="name">${this.sanitize(stepPoint.title)}</div>`;
		} else {
			return '';
		}
	}
}
